import React from 'react';
import Row from 'components/Row';
import Col from 'components/Col';
import LayoutSelect from 'components/forms/layoutSelect';
import LayoutInput from 'components/forms/layoutInput';
import LayoutCheckbox from 'ui/components/forms/layoutCheckbox';
import Round from './Round';
import Rectangle from './Rectangle';
import Oval from './Oval';
import Kidney from './Kidney';
import Button from 'components/layout/Button';
import ValidationGroup from 'helpers/validation';
import {inRange, poolShapes, poolShapeLabelIDs} from './helpers';
import {FormattedMessage, FormattedNumber, injectIntl, intlShape} from 'react-intl';
import DealerApi from 'api/dealer';
import session from 'services/sessionService';
import {countries, calculationUnits} from 'helpers/constants';
import {gallonsToLitres, metersToFeets} from 'helpers/dataHelpers';

class PoolVolume extends React.Component {
  static initialState = {
    poolShape: '',
    slopingSides: false,
    deepDepth: undefined,
    shallowDepth: undefined,
    volume: undefined,
    dealer: {
      country: countries.US,
    },
    calculationUnit: calculationUnits.feet,
    isCA: false,
  };

  static propTypes = {
    intl: intlShape,
  };

  constructor(props) {
    super(props);

    this.group = new ValidationGroup(this.calculate);
  }

  state = PoolVolume.initialState;

  componentWillMount() {
    DealerApi.getDealer(session.getUser().dealerId)
      .then(dealer => this.setState({
        dealer: dealer, 
        isCA: dealer.country === countries.CA
      }));
  }

  update = ({key, value}) => {
    this.setState({[key]: value});
  };

  changeCalculationUnit = unit => {
    this.setState({calculationUnit: unit.value});
  };

  reset = () => {
    this.setState(PoolVolume.initialState);
    this.shape && this.shape.getWrappedInstance().reset();
  };

  calculate = () => {
    let {deepDepth, shallowDepth, slopingSides, calculationUnit} = this.state;

    if(calculationUnit === calculationUnits.meter) {
      deepDepth = metersToFeets(deepDepth);
      shallowDepth = metersToFeets(shallowDepth);
    }

    const averageDepth = (parseFloat(deepDepth) + parseFloat(shallowDepth)) / 2;
    const volume = this.shape ? this.shape.getWrappedInstance().volume(averageDepth) / (slopingSides ? 1.15 : 1) : undefined;

    this.setState({volume});
  };

  render() {
    const {dealer, poolShape, deepDepth, shallowDepth, slopingSides, volume, isCA, calculationUnit} = this.state;
    const {formatMessage} = this.props.intl;
    const unit = calculationUnit === calculationUnits.meter ? formatMessage({id: 'general.unit.meter'}) : formatMessage({id: 'general.unit.feet'});

    return <div className='pool-calc-page'>
      <p className='pool-calc-page__instructions'>{formatMessage({id: 'quick-calcs.pool-volume.instructions'}, {unit: unit.toLowerCase()})}</p>

      <div className='pool-calc-page__input'>
        <Row>
          <Col md={4}>
            <LayoutSelect label={formatMessage({id: 'quick-calcs.pool-volume.shape'})}
                          value={poolShape}
                          required
                          dataKey='poolShape'
                          dataChanged={this.update}
                          group={this.group}
                          options={[''].concat(Object.values(poolShapeLabelIDs).map(id => formatMessage({id})))}
                          keys={[''].concat(Object.keys(poolShapeLabelIDs))} />
          </Col>

          <Col md={4}>
            <LayoutSelect label={formatMessage({id: 'general.uom'})}
                          value={calculationUnit}
                          dataKey='units'
                          dataChanged={this.changeCalculationUnit}
                          options={[formatMessage({id: 'general.unit.feet'}), formatMessage({id: 'general.unit.meter'})]} 
                          keys={[calculationUnits.feet, calculationUnits.meter]} />
          </Col>

          {poolShape === poolShapes.rectangle ? <Rectangle group={this.group} ref={r => this.shape = r} unit={calculationUnit} /> : null}
          {poolShape === poolShapes.round ? <Round group={this.group} ref={r => this.shape = r} unit={calculationUnit} /> : null}
          {poolShape === poolShapes.oval ? <Oval group={this.group} ref={r => this.shape = r} unit={calculationUnit} /> : null}
          {poolShape === poolShapes.kidney ? <Kidney group={this.group} ref={r => this.shape = r} unit={calculationUnit} /> : null}

          <Col md={4}>
            <LayoutInput dataKey='deepDepth'
                         toolTip={formatMessage({id: 'quick-calcs.pool-volume.deep.tooltip'}, {unit: unit.toLowerCase()})}
                         label={formatMessage({id: 'quick-calcs.pool-volume.depth.deep'})}
                         type='number'
                         step='0.01'
                         validate={inRange}
                         group={this.group}
                         required
                         value={deepDepth}
                         dataChanged={this.update} />
          </Col>

          <Col md={4}>
            <LayoutInput dataKey='shallowDepth'
                         toolTip={formatMessage({id: 'quick-calcs.pool-volume.shallow.tooltip'}, {unit: unit.toLowerCase()})}
                         label={formatMessage({id: 'quick-calcs.pool-volume.depth.shallow'})}
                         type='number'
                         step='0.01'
                         validate={inRange}
                         group={this.group}
                         required
                         value={shallowDepth}
                         dataChanged={this.update} />
          </Col>

          <Col md={4}>
            <LayoutCheckbox label={formatMessage({id: 'quick-calcs.pool-volume.sloping'})}
                            value={slopingSides}
                            dataChanged={this.update}
                            group={this.group}
                            topLabel=' '
                            dataKey='slopingSides' />
          </Col>
        </Row>
      </div>

      <Row>
        <Col md={12}>
          <div className='pull-right pool-calc-page__buttons'>
            <Button link onClick={this.reset}><FormattedMessage id='quick-calcs.reset' /></Button>
            <Button primary onClick={this.group.validate}><FormattedMessage id='quick-calcs.calculate' /></Button>
          </div>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <div className='pool-calc-page__result'>
            <span className='pool-calc-page__result--title'><FormattedMessage id='quick-calcs.pool-volume.new' />:</span>
            <span className='pool-calc-page__result--amount'>{volume && !isCA ?
              <React.Fragment><FormattedNumber value={volume} /> gal(s)</React.Fragment> :
              volume ?
                <React.Fragment><FormattedNumber value={gallonsToLitres(volume)}/> L(s)</React.Fragment> :
                null}</span>
          </div>
        </Col>
      </Row>
    </div>;
  }
}

export default injectIntl(PoolVolume);
